export type ButtonVariant =
  | "primary"
  | "secondary"
  | "success"
  | "danger"
  | "dangerSoft"
  | "ghost"
  | "soft"
  | "link"
  | "audit"
  | "trace"
  | "info";

export type ButtonSize = "sm" | "md" | "lg" | "icon";

export interface ButtonStyleOptions {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  className?: string;
}

const baseClasses =
  "relative inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-xl border font-semibold shadow-sm outline-none transition-[color,background-color,border-color,box-shadow,transform] duration-150 focus-visible:ring-2 focus-visible:ring-[var(--color-focus-ring)] focus-visible:ring-offset-2 active:translate-y-px disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50";

const variantClasses: Record<ButtonVariant, string> = {
  primary:
    "border-[var(--color-brand-700)] bg-[var(--color-brand-700)] text-white hover:border-[var(--color-brand-800)] hover:bg-[var(--color-brand-800)]",
  secondary:
    "border-[var(--color-border-default)] bg-[var(--color-surface-card)] text-[var(--color-text-primary)] hover:border-[var(--color-brand-600)] hover:bg-[var(--color-brand-50)] hover:text-[var(--color-brand-800)]",
  success:
    "border-[var(--color-brand-700)] bg-[var(--color-brand-700)] text-white hover:border-[var(--color-brand-800)] hover:bg-[var(--color-brand-800)]",
  danger:
    "border-[var(--color-danger-600)] bg-[var(--color-danger-600)] text-white hover:border-[var(--color-danger-700)] hover:bg-[var(--color-danger-700)] focus-visible:ring-[var(--color-danger-600)]",
  dangerSoft:
    "border-[var(--color-danger-200)] bg-[var(--color-danger-50)] text-[var(--color-danger-700)] hover:border-[var(--color-danger-600)] hover:bg-[var(--color-danger-200)] focus-visible:ring-[var(--color-danger-600)]",
  ghost:
    "border-transparent bg-transparent text-[var(--color-text-secondary)] shadow-none hover:bg-[var(--color-surface-muted)] hover:text-[var(--color-text-primary)]",
  soft: "border-[var(--color-brand-200)] bg-[var(--color-brand-50)] text-[var(--color-brand-800)] hover:border-[var(--color-brand-500)] hover:bg-[var(--color-brand-100)]",
  link: "rounded-md border-transparent bg-transparent !px-0 text-[var(--color-brand-700)] shadow-none hover:text-[var(--color-brand-900)] hover:underline hover:underline-offset-4",
  audit:
    "border-[var(--color-audit-600)] bg-[var(--color-audit-600)] text-white hover:border-[var(--color-audit-700)] hover:bg-[var(--color-audit-700)] focus-visible:ring-[var(--color-audit-600)]",
  trace:
    "border-[var(--color-brand-700)] bg-[var(--color-brand-700)] text-white hover:border-[var(--color-brand-800)] hover:bg-[var(--color-brand-800)]",
  info: "border-[var(--color-info-600)] bg-[var(--color-info-600)] text-white hover:border-[var(--color-info-700)] hover:bg-[var(--color-info-700)] focus-visible:ring-[var(--color-info-600)]",
};

const sizeClasses: Record<ButtonSize, string> = {
  sm: "min-h-9 px-3 py-1.5 text-sm",
  md: "min-h-10 px-3.5 py-2 text-sm",
  lg: "min-h-11 px-4 py-2.5 text-base",
  icon: "h-10 w-10 p-0",
};

export function buttonStyles({
  variant = "secondary",
  size = "md",
  fullWidth = false,
  className = "",
}: ButtonStyleOptions = {}) {
  return [
    baseClasses,
    variantClasses[variant],
    sizeClasses[size],
    fullWidth ? "w-full" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");
}
